import { CircularProgress } from '@mui/material';
import { useEditSeasonMutation } from 'common/api/endpoints/harvestData';
import { handleError } from 'common/api/utils/handleError';
import { Checkbox } from 'common/components/Checkbox/Checkbox';
import FormPrompt from 'common/components/FormPrompt';
import { Season } from 'common/models/season';
import * as notifier from 'common/services/notification';
import { FC, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  ModalError,
  SeasonModalForm,
  YearCloseBtn,
  YearOptionsContainer,
  YearOptionWrapper,
  YearSaveBtn,
} from './styles';

export type SeasonsSelected = Record<string, boolean>;

export const SeasonSelectModal: FC<{
  closeModal: () => void;
  seasons: Season[];
}> = ({ closeModal, seasons }) => {
  const [requiredError, setRequiredError] = useState('');
  const [maxSeasonError, setMaxSeasonError] = useState('');
  const [editActiveSeason, { isLoading }] = useEditSeasonMutation();
  const isError = !!(requiredError || maxSeasonError);

  const defaultValues: SeasonsSelected = useMemo(() => {
    return seasons.reduce((acc, season) => {
      acc[season.id.toString()] = season.isActive;
      return acc;
    }, {} as SeasonsSelected);
  }, [seasons]);

  const methods = useForm<SeasonsSelected>({
    defaultValues,
  });
  const {
    getFieldState,
    handleSubmit,
    formState: { isDirty },
    watch,
  } = methods;
  const onSubmit = async (data: SeasonsSelected): Promise<void> => {
    if (!isDirty) {
      closeModal();
      return;
    }

    if (!isError) {
      const dto = Object.entries(data).reduce(
        (acc, [id, isActive]) => {
          if (getFieldState(id).isDirty) {
            acc.push({
              id: parseInt(id, 10),
              isActive,
            });
          }
          return acc;
        },
        [] as Pick<Season, 'id' | 'isActive'>[],
      );

      try {
        await editActiveSeason(dto).unwrap();
        notifier.showSuccessMessage('Seasons to sync updated.');
        closeModal();
      } catch (e) {
        handleError(e, 'Unable to update seasons to sync.');
      }
    }
  };

  // Validation is done here instead of through 'yup' because we are setting
  // global form errors through form field values.
  useEffect(() => {
    const watchedForm = watch(values => {
      const isActiveArr = Object.values(values).filter(isActive => isActive);

      if (isActiveArr.length === 0) {
        setRequiredError('At least one season must be selected.');
      } else if (requiredError) {
        setRequiredError('');
      }

      if (isActiveArr.length > 2) {
        setMaxSeasonError('Only two seasons can be synced at a time.');
      } else if (maxSeasonError) {
        setMaxSeasonError('');
      }
    });

    return () => watchedForm.unsubscribe();
  }, [maxSeasonError, requiredError, watch]);

  return (
    <FormProvider {...methods}>
      <SeasonModalForm onSubmit={handleSubmit(onSubmit)}>
        <h5>Select seasons for sync:</h5>
        <YearOptionsContainer isError={isError}>
          {seasons.length > 0 ? (
            seasons.map(season => (
              <YearOptionWrapper key={season.year}>
                <Checkbox
                  formProps={{ name: season.id.toString() }}
                  isDisabled={isLoading}
                />
                <label htmlFor={season.year.toString()}>{season.year}</label>
              </YearOptionWrapper>
            ))
          ) : (
            <span>There was an issue fetching seasons.</span>
          )}
        </YearOptionsContainer>
        {requiredError && <ModalError>{requiredError}</ModalError>}
        {maxSeasonError && <ModalError>{maxSeasonError}</ModalError>}
        <div style={{ display: 'flex', gap: '10px' }}>
          <YearCloseBtn type='button' onClick={closeModal} disabled={isLoading}>
            Cancel
          </YearCloseBtn>
          <YearSaveBtn type='submit' disabled={isLoading || isError}>
            {isLoading && <CircularProgress size='15px' />}
            <p style={{ margin: 0 }}>Save</p>
          </YearSaveBtn>
        </div>
      </SeasonModalForm>
      <FormPrompt isDirty={isDirty} isSubmitting={isLoading} />
    </FormProvider>
  );
};
